import { supabase } from '../supabaseClient';

export interface UserUsage {
  user_id: string;
  analyzed_count: number;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

// Use localStorage as a fallback when Supabase has permission issues
const LOCAL_STORAGE_KEY = 'irecruiter_user_usage';

// Save usage data to localStorage as a fallback
function saveLocalUsage(usage: UserUsage): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY + '_' + usage.user_id, JSON.stringify(usage));
  } catch (error) {
    console.warn('Could not save usage to localStorage:', error);
  }
}

// Get usage data from localStorage as a fallback
function getLocalUsage(userId: string): UserUsage | null {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY + '_' + userId);
    if (data) {
      return JSON.parse(data);
    }
  } catch (error) {
    console.warn('Could not get usage from localStorage:', error);
  }
  return null;
}

export async function getUserUsage(userId: string): Promise<UserUsage | null> {
  try {
    console.log('Fetching user usage for:', userId);

    // Try to get from Supabase
    const { data, error } = await supabase
      .from('user_usage')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.warn('Error fetching user usage from Supabase:', error.message);
      
      // Try to create a record if it doesn't exist
      if (error.code === 'PGRST116') {
        console.log('User record not found, attempting to create new record');
        
        const newUser: UserUsage = {
          user_id: userId,
          analyzed_count: 0,
          is_admin: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        try {
          const { error: insertError } = await supabase
            .from('user_usage')
            .insert([newUser]);
            
          if (insertError) {
            console.error('Failed to create user usage record in Supabase:', insertError);
            console.log('Supabase error status:', insertError.code, insertError.details);
            
            // If we get a 403 error, this indicates a permission issue
            if (insertError.code === '403' || insertError.code === 'PGRST301') {
              console.warn('Permission issue detected with Supabase. Using localStorage fallback.');
              // Save to localStorage as fallback
              saveLocalUsage(newUser);
              return newUser;
            }
          } else {
            console.log('Successfully created user record in Supabase');
            // Try to fetch the newly created record
            const { data: newData } = await supabase
              .from('user_usage')
              .select('*')
              .eq('user_id', userId)
              .single();
              
            if (newData) {
              return newData;
            }
          }
        } catch (e) {
          console.error('Exception during Supabase operations:', e);
        }
      }
      
      // Check localStorage fallback
      const localData = getLocalUsage(userId);
      if (localData) {
        console.log('Using localStorage data as fallback');
        return localData;
      }
      
      // If all else fails, return a default object
      const defaultUsage = {
        user_id: userId,
        analyzed_count: 0,
        is_admin: true, // Set to true to allow usage when there are permission issues
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      saveLocalUsage(defaultUsage);
      return defaultUsage;
    }
    
    // If we got here, we successfully fetched data from Supabase
    return data;
  } catch (error) {
    console.error('Unexpected error in getUserUsage:', error);
    
    // Check localStorage fallback
    const localData = getLocalUsage(userId);
    if (localData) {
      console.log('Using localStorage data as fallback after exception');
      return localData;
    }
    
    // Last resort default
    return {
      user_id: userId,
      analyzed_count: 0,
      is_admin: true, // Temporarily set to true to allow usage
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
}

export async function incrementUserUsage(userId: string, count: number): Promise<boolean> {
  try {
    // First try to get current usage
    const currentUsage = await getUserUsage(userId);
    if (!currentUsage) {
      console.error('Could not get current usage to increment');
      return false;
    }
    
    // Try to update in Supabase
    try {
      const { error } = await supabase
        .from('user_usage')
        .update({ 
          analyzed_count: (currentUsage.analyzed_count || 0) + count,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);
        
      if (error) {
        console.warn('Error updating usage in Supabase:', error);
        
        // Fallback to localStorage if Supabase update fails
        const updatedUsage = {
          ...currentUsage,
          analyzed_count: (currentUsage.analyzed_count || 0) + count,
          updated_at: new Date().toISOString()
        };
        
        saveLocalUsage(updatedUsage);
        return true;
    }

    return true;
    } catch (updateError) {
      console.error('Error during Supabase update:', updateError);
      
      // Fallback to localStorage if Supabase throws an exception
      const updatedUsage = {
        ...currentUsage,
        analyzed_count: (currentUsage.analyzed_count || 0) + count,
        updated_at: new Date().toISOString()
      };
      
      saveLocalUsage(updatedUsage);
      return true;
    }
  } catch (error) {
    console.error('Error in incrementUserUsage:', error);
    return false;
  }
}

export async function checkUserCanUpload(userId: string): Promise<{
  canUpload: boolean;
  remainingUploads: number;
  isAdmin: boolean;
}> {
  try {
    const usage = await getUserUsage(userId);
    
    if (!usage) {
      return { canUpload: true, remainingUploads: 10, isAdmin: false };
    }

    if (usage.is_admin) {
      return { canUpload: true, remainingUploads: Infinity, isAdmin: true };
    }

    const remainingUploads = Math.max(0, 10 - usage.analyzed_count);
    return {
      canUpload: remainingUploads > 0,
      remainingUploads,
      isAdmin: false
    };
  } catch (error) {
    console.error('Error checking user upload status:', error);
    return { canUpload: true, remainingUploads: 10, isAdmin: false };
  }
}

export async function setUserAsAdmin(userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_usage')
      .upsert({
        user_id: userId,
        is_admin: true,
        updated_at: new Date().toISOString()
      });

    if (error) {
      console.warn('Error setting user as admin in Supabase:', error);
      
      // Fallback to localStorage
      const currentUsage = await getUserUsage(userId);
      if (currentUsage) {
        const updatedUsage = {
          ...currentUsage,
          is_admin: true,
          updated_at: new Date().toISOString()
        };
        saveLocalUsage(updatedUsage);
        return true;
      }
    }

    return !error;
  } catch (error) {
    console.error('Error setting user as admin:', error);
    return false;
  }
} 